import { useSignal } from "@preact/signals";
import { AuthLayout } from "../components/layout";
import { Button, Form, type FormErrors, Input } from "../components/ui";
import { usePageTitle } from "../hooks";
import { rpc } from "../rpc";

type FormState = {
  errors: Record<string, string>;
  loading: boolean;
  success: boolean;
  email: string;
};

export function Page() {
  usePageTitle("Sign Up");
  const state = useSignal<FormState>({
    errors: {},
    loading: false,
    success: false,
    email: "",
  });

  // FormErrors-compatible lens for Form component
  const errors = {
    get value() {
      return state.value.errors;
    },
    set value(v: Record<string, string>) {
      state.value = { ...state.value, errors: v };
    },
  } as FormErrors;

  async function handleSubmit(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const email = formData.get("email") as string;

    // Client-side validation
    const newErrors: Record<string, string> = {};
    if (!email.trim()) {
      newErrors.email = "Email is required";
    }

    if (Object.keys(newErrors).length > 0) {
      state.value = { ...state.value, errors: newErrors };
      return;
    }

    state.value = { ...state.value, errors: {}, loading: true };

    try {
      await rpc.auth.signup({ email });
      state.value = { errors: {}, loading: false, success: true, email };
    } catch (err: unknown) {
      if (err && typeof err === "object" && "message" in err) {
        const error = err as { message: string; code?: string; field?: string };
        if (error.field) {
          state.value = {
            ...state.value,
            errors: { [error.field]: error.message },
            loading: false,
          };
        } else {
          state.value = {
            ...state.value,
            errors: { form: error.message },
            loading: false,
          };
        }
      } else {
        state.value = {
          ...state.value,
          errors: { form: "Something went wrong" },
          loading: false,
        };
      }
    }
  }

  // Show success message after submitting
  if (state.value.success) {
    return (
      <AuthLayout title="Check your email" hideLogo>
        <div class="text-center">
          <p class="text-text-muted">
            We sent a verification link to{" "}
            <strong class="text-text">{state.value.email}</strong>.
          </p>
          <p class="mt-6 text-sm text-text-muted">
            Didn't receive it?{" "}
            <button
              type="button"
              class="link"
              onClick={() =>
                (state.value = {
                  errors: {},
                  loading: false,
                  success: false,
                  email: "",
                })
              }
            >
              Try again
            </button>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Create account" subtitle="Get started with Pulse">
      <Form errors={errors} onSubmit={handleSubmit} class="flex flex-col gap-6">
        {errors.value.form && (
          <div class="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {errors.value.form}
          </div>
        )}

        <Input
          label="Email"
          name="email"
          type="email"
          autocomplete="email"
          autofocus
          required
        />

        <Button type="submit" loading={state.value.loading}>
          Continue
        </Button>

        <p class="text-center text-sm text-text-muted">
          Already have an account?{" "}
          <a href="/login" class="link">
            Sign in
          </a>
        </p>
      </Form>
    </AuthLayout>
  );
}
